import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { LoadDictionary, LoadDictionaryField, LoadDictionaryTable } from "@/api/integration";
import { Card, SectionHead } from "@/components/brand/Card";
import { Pill } from "@/components/brand/Pill";
import { Toolbar, ToolbarChip, ToolbarSearch } from "@/components/brand/Toolbar";

import { domainLabel } from "./domainLabel";
import { fieldDescription, fieldShape } from "./fieldText";

const LOAD_BATCH_PATH = "/api/load/v1/projects/{project_id}/loads/{load_id}/tables/";

/**
 * A tabela inteira quando a busca bate no nome dela; senão, só os campos que batem no nome ou
 * na descrição exibida.
 */
function visibleFields(t: TFunction, table: LoadDictionaryTable, query: string) {
  if (!query || table.name.toLowerCase().includes(query)) return table.fields;
  return table.fields.filter(
    (field) =>
      field.name.toLowerCase().includes(query) ||
      fieldDescription(t, field).toLowerCase().includes(query),
  );
}

export function LoadDictionaryView({ dictionary }: { dictionary: LoadDictionary }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState<string | null>(null);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    return dictionary.domains
      .filter((d) => domain === null || d.name === domain)
      .map((d) => ({
        name: d.name,
        tables: d.tables
          .map((table) => ({ table, fields: visibleFields(t, table, q) }))
          .filter(({ fields }) => fields.length > 0),
      }))
      .filter((d) => d.tables.length > 0);
  }, [dictionary, query, domain, t]);

  return (
    <div className="space-y-7">
      <Card>
        <Toolbar className="flex-wrap">
          <ToolbarSearch
            placeholder={t("Search table, field or description…")}
            width={320}
            value={query}
            onChange={setQuery}
          />
          <ToolbarChip active={domain === null} onClick={() => setDomain(null)}>
            {t("All")}
          </ToolbarChip>
          {dictionary.domains.map((d) => (
            <ToolbarChip key={d.name} active={domain === d.name} onClick={() => setDomain(d.name)}>
              {domainLabel(t, d.name)}
            </ToolbarChip>
          ))}
        </Toolbar>
      </Card>

      {sections.length === 0 && (
        <p className="text-center py-12 text-[#7b8799] text-sm">
          {t("No table or field matches your search.")}
        </p>
      )}

      {sections.map((section) => (
        <section key={section.name} className="space-y-4">
          <SectionHead
            overline={t("DOMAIN")}
            title={domainLabel(t, section.name)}
            tag={`${section.tables.length} ${t("tables")}`}
          />
          {section.tables.map(({ table, fields }) => (
            <TableCard key={table.name} table={table} fields={fields} />
          ))}
        </section>
      ))}
    </div>
  );
}

function TableCard({ table, fields }: { table: LoadDictionaryTable; fields: LoadDictionaryField[] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <div data-testid={`dictionary-table-${table.name}`}>
        <div className="px-4 py-3 border-b border-[#f2f4f7] flex flex-col gap-1">
          <span className="mono text-[14px] font-semibold text-[#08254c]">{table.name}</span>
          <span className="mono text-[11px] text-[#7b8799] break-all">
            PUT {LOAD_BATCH_PATH}
            {table.name}/batches/{"{batch_number}"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: "26%" }}>{t("Field")}</th>
                <th style={{ width: "20%" }}>{t("Type")}</th>
                <th style={{ width: "12%" }}>{t("Required")}</th>
                <th>{t("Description")}</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr key={field.name}>
                  <td>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="mono text-[13px] text-[#08254c]">{field.name}</span>
                      {field.key && (
                        <span data-testid={`field-${field.name}-key`}>
                          <Pill tone="gold">{t("key")}</Pill>
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="mono text-[12px] text-[#596b85]">{fieldShape(field)}</span>
                    {field.nullable && <div className="mono muted text-[10px]">null</div>}
                    {field.values && (
                      <div className="mono muted text-[10px] break-all">{field.values.join(" | ")}</div>
                    )}
                  </td>
                  <td>
                    {field.required ? (
                      <Pill tone="navy">{t("required")}</Pill>
                    ) : (
                      <span className="muted text-[12px]">{t("optional")}</span>
                    )}
                  </td>
                  <td className="text-[13px] text-[#08254c]">{fieldDescription(t, field)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
